<template>
  <div>
    <Teleport to="body">
      <dialog-base class="wrapper" :class="{ 'paa-dialog': !!question }" :show="isOpenDialog" @hide="hideDialog"
        :is-draggable="true" :close-on-click="true" size="lg" :center="true" test-id="card-aioverview-modal">
        <template #header>
          <div class="d-flex align-items-center">
            <img :src="AiOverviewLogo" />
            <span v-if="question && date" class="paa-date ms-auto" data-testid="card-aioverview-paa-date">{{ date }}</span>
          </div>
        </template>

        <template #content>
          <!-- DM-10271: layout and colours follow the ticket's prototype -->
          <h2 v-if="question && show" class="paa-question" :style="questionFill" data-testid="card-aioverview-paa-question">
            {{ question.title }}
          </h2>
          <div v-if="html || !question" class="dialog-content" ref="dialogContent" v-html="html"></div>
          <!-- an answer with no html view shows its text the way the card does:
               AI-overview text as text, a plain answer as its html -->
          <template v-else-if="show">
            <div v-if="isAiOverviewText(question.desc)" class="paa-plain-answer">{{ question.desc }}</div>
            <div v-else class="paa-plain-answer" v-html="question.desc"></div>
          </template>

          <!-- the client's rating of the question, and the answer's sources -->
          <template v-if="question && show">
            <div class="paa-rating-box">
              <div class="paa-rating-head">
                <span class="paa-rating-title">Question rating</span>
                <span class="paa-rating-help">Applies to this question on every date it appears, whatever the answer text that day</span>
              </div>
              <div class="paa-rating-buttons">
                <button v-for="option in questionRatingOptions" :key="option.value" type="button" class="paa-rating-btn"
                  :disabled="!markupEditable || questionRating === option.value"
                  :aria-pressed="questionRating === option.value ? 'true' : 'false'"
                  :style="questionButtonStyle(option)"
                  :data-testid="`card-aioverview-paa-rating-${option.name.toLowerCase()}-btn`"
                  @click="setQuestionRating(option.value)">
                  {{ option.name }}
                </button>
              </div>
            </div>

            <div class="paa-sources">
              <div class="paa-sources-head">
                <span class="paa-sources-title">Sources ({{ sources.length }})</span>
                <span v-for="name in chipColumns" :key="name" class="paa-chip-column">{{ name }}</span>
              </div>
              <div v-for="(source, k) in sources" :key="k" class="paa-source-row" :style="{ background: sourceFill(source) }"
                data-testid="card-aioverview-paa-source-row">
                <div class="paa-source-main">
                  <favicon :size="18" :url="source.url" />
                  <div class="paa-source-text">
                    <a :href="source.url" target="_blank" :title="source.url">{{ source.title || displayUrl(source.url) }}</a>
                    <span v-if="source.title">{{ displayUrl(source.url) }}</span>
                  </div>
                </div>
                <source-markup-chips :source="source" :result-type="question.resulttype || 'questionanswer'" :ts="ts" />
              </div>
            </div>
          </template>
        </template>
      </dialog-base>
    </Teleport>
  </div>
</template>

<script>
import DialogBase from "@/components/modals/DialogBase.vue";
import Favicon from "@/components/Favicon.vue";
import SourceMarkupChips from "@/components/card/SourceMarkupChips.vue";
import { nextTick } from 'vue';
import { isAiOverviewText, paaSources } from "@/utils/aiOverview";
import { mapGetters } from "vuex";
import { RATING_OPTIONS, UNMARKED, isGroupVisible, markupFill } from "@/utils/markupOptions";


export default {
  // DM-10271: only the Timeline view can change markup (see SourceMarkupChips)
  inject: { markupEditable: { default: false } },
  components: {
    DialogBase,
    Favicon,
    SourceMarkupChips,
  },
  emits: ['hideDialog'],
  data() {
    return {
      AiOverviewLogo: require('@/assets/images/ai-overview-logo.png'),
      dialogContent: null,
      originalDialogHtml: '',
    }
  },
  props: {
    html: {
      type: String,
      required: false,
    },
    show: Boolean,
    isPAA: {
      type: Boolean,
      required: false,
      default: false,
    },
    // DM-10271: the People-Also-Ask question whose answer this is (title, urls, questionMarkup);
    // shows its rating and sources. Absent for the AI Overview panel.
    question: {
      type: Object,
      required: false,
      default: null,
    },
    date: {
      type: String,
      required: false,
      default: "",
    },
    ts: { // the card's date (unix seconds): a source chip marks the url as of this date
      type: Number,
      required: false,
      default: 0,
    },
  },
  computed: {
    ...mapGetters("markup", { markupLabels: "markupLabels" }),
    ...mapGetters("toolbar", { isMarkupEnabled: "isMarkupEnabled" }),
    ...mapGetters("routeParams", { userInfo: "userInfo" }),
    // the toolbar's markup mode ('' when markup is off): the source rows take that markup's fill
    markupMode() {
      return this.markupLabels?.[0] || "";
    },
    chipColumns() {
      const context = {
        isMarkupEnabled: this.isMarkupEnabled, isSu: this.userInfo?.is_su, resultType: this.question?.resulttype,
      };
      return ["rating", "ownership", "authority"]
        .filter((name) => isGroupVisible(name, context))
        .map((name) => name.charAt(0).toUpperCase() + name.slice(1));
    },
    isOpenDialog() {
      return this.show;
    },
    questionRatingOptions() {
      return RATING_OPTIONS;
    },
    questionRating() {
      return this.question?.questionMarkup?.rating ?? UNMARKED.rating;
    },
    questionFill() {
      const option = RATING_OPTIONS.find((o) => o.value === this.questionRating);
      return { background: option?.fill || "#f1f3f5" };
    },
    sources() {
      // the card's rule (paaSources); a single-source answer's own title is the question, not the page's
      return paaSources(this.question).map((source) =>
        source === this.question ? { url: source.url, urlid: source.urlid, markup: source.markup } : source);
    },
  },

  methods: {
    isAiOverviewText,
    sourceFill(source) {
      return this.markupMode ? markupFill(source.markup, this.markupMode) : "#fff";
    },
    // the Markup dialog's rating buttons; a selected Unmarked keeps dark text on its grey
    questionButtonStyle(option) {
      const selected = option.value === this.questionRating;
      return {
        borderColor: option.color,
        background: selected ? option.color : "#fff",
        color: selected ? (option.value === UNMARKED.rating ? "#262C34" : "#fff") : "#212529",
        cursor: this.markupEditable && !selected ? "pointer" : "default",
      };
    },
    displayUrl(url) {
      return (url || "").replace(/^https?:\/\/(www\.)?/, "");
    },
    setQuestionRating(score) {
      if (!this.markupEditable || score === this.questionRating) return;
      this.$store.dispatch("markup/rateQuestion", { question: this.question.title, score })
        .catch((e) => this.$notifier.error({ message: "Failed setting the question rating", e }));
    },

    // ============ Helper Methods ============

    /**
     * Normalize a URL for deduplication comparison.
     * Extracts the actual destination from Google redirect URLs and strips query params/fragments.
     */
    normalizeUrl(href) {
      if (!href) return null;
      let baseHref = href;
      const urlMatch = href.match(/[?&]url=([^&]+)/);
      if (urlMatch) {
        try {
          baseHref = decodeURIComponent(urlMatch[1]);
        } catch (e) {
          baseHref = urlMatch[1];
        }
      }
      return baseHref.split('#')[0].split('?')[0];
    },

    /**
     * Create a styled toggle button (Show More / Show Less).
     */
    createToggleButton(text, className) {
      const btn = document.createElement("button");
      btn.textContent = text;
      btn.classList.add(className);
      Object.assign(btn.style, {
        display: "block",
        width: "50%",
        cursor: "pointer",
        backgroundColor: "transparent",
        border: "1px #a3c9ff solid",
        borderRadius: "999rem",
        marginTop: "1rem"
      });
      return btn;
    },

    /**
     * Fix lazy-loaded images by copying data-src to src.
     */
    fixLazyLoadedImages(container) {
      container.querySelectorAll('img').forEach(img => {
        const src = img.getAttribute('src');
        if (!src || src.includes('data:image/gif')) {
          const dataSrc = img.getAttribute('data-src');
          if (dataSrc) {
            img.setAttribute('src', dataSrc);
          }
        }
      });
    },

    /**
     * Unhide an element by removing ahbak/aria-hidden attributes.
     */
    unhideElement(el) {
      el.removeAttribute('ahbak');
      el.removeAttribute('aria-hidden');
      el.style.display = '';
    },

    /**
     * Copy the last div (typically a border/styling div) from the first li in a ul to a target li.
     */
    copyBorderDivToLi(sourceUl, targetLi) {
      const firstLi = sourceUl.querySelector('li');
      if (firstLi) {
        const lastDiv = firstLi.querySelector(':scope > div:last-of-type');
        if (lastDiv) {
          targetLi.appendChild(lastDiv.cloneNode(true));
        }
      }
    },

    /**
     * Apply flex styling to a source li element to arrange text and image side by side.
     */
    applyFlexStylingToLi(li) {
      const firstDivInLi = li.querySelector('div');
      if (firstDivInLi) {
        const contentWrapper = firstDivInLi.querySelector(':scope > div');
        if (contentWrapper) {
          const hasImage = contentWrapper.querySelector('img');
          const hasTextContent = contentWrapper.querySelector('div');
          if (hasImage && hasTextContent) {
            contentWrapper.style.display = 'flex';
            contentWrapper.style.justifyContent = 'space-between';
            contentWrapper.style.alignItems = 'flex-start';
            contentWrapper.style.gap = '1rem';

            const imageContainer = Array.from(contentWrapper.children).find(child =>
              child.querySelector('img') && child.style.getPropertyValue('--thumbnail-width')
            );
            if (imageContainer) {
              imageContainer.style.flexShrink = '0';
            }
          } 
        }
      }

      // Style the favicon row - make favicon and source name side-by-side
      // Find structurally: a div containing a favicon img with sibling divs
      const faviconImg = li.querySelector('img[data-src*="favicon"], img[src*="favicon"]');
      if (faviconImg) {
        // Walk up to find the container that has multiple children (favicon + text divs)
        let faviconRow = faviconImg.parentElement;
        while (faviconRow && faviconRow !== li) {
          // Look for a div with multiple direct div children (favicon container + text container)
          const directDivChildren = Array.from(faviconRow.children).filter(c => c.tagName === 'DIV');
          if (directDivChildren.length >= 2) {
            faviconRow.style.display = 'flex';
            faviconRow.style.alignItems = 'center';
            faviconRow.style.gap = '0.5rem';
            break;
          }
          faviconRow = faviconRow.parentElement;
        }
      }
    },

    // ============ Dialog Lifecycle ============

    hideDialog() {
      this.showDialog = false;
      this.$emit('hideDialog');
      if (this.dialogContent) {
        this.dialogContent.style.visibility = 'hidden';
        this.dialogContent.innerHTML = this.originalDialogHtml;
      }
      const showMoreButton = document.querySelector('.show-more-button');
      if (showMoreButton) {
        showMoreButton.remove();
      }
      const showLessButton = document.querySelector('.show-less-button');
      if (showLessButton) {
        showLessButton.remove();
      }
    },
    async onDialogShow(newValue) {
      if (newValue) {
        await nextTick();
        this.dialogContent = this.$refs.dialogContent;
        if (this.dialogContent) {
          this.dialogContent.style.visibility = 'visible';
          this.originalDialogHtml = this.dialogContent.innerHTML;
          this.applyPersistentHtmlChanges();
        }
      }
    },

    applyPersistentHtmlChanges() {
      this.unwrapEntityMentions();
      if (this.isPAA) {
        // Must run before removeButtons()/the cursor:pointer sweep, which
        // would otherwise delete the citation chips (they are <button>s) and
        // the media carousels' role="button" image wrappers.
        this.preserveCitationChips();
        this.tagMediaLists();
      }
      this.applyMinWidth();
      this.handleScroll();
      this.removeTitle();
      this.removeTopPadding();
      this.fixHighlightColor();
      this.removeSvgs();
      this.addShowMoreButton();
      this.removeUnwantedText();
      const isHorizontal = this.hasHorizontalSourcesList();
      if (!this.isPAA && !isHorizontal) {
        this.fixGridElements();
        this.formatExpandedView();
      }
      const as = document.querySelectorAll('a');
      if (as.length) {
        as.forEach(a => {
          const href = a.getAttribute('href');
          if (href && !href.includes("google")) {
            a.setAttribute('target', '_blank');
          } else if (href) {
            a.removeAttribute('href');
          }
        })
      }

      if (this.isPAA || this.hasHorizontalSourcesList()) {
        if (this.isPAA) {
          this.formatPaaNativeLayout();
        } else {
          this.fixLinks();
        }

        const button = this.dialogContent.querySelectorAll('*:not(a)');
        const showMore = Array.from(button).filter(el => {
          return getComputedStyle(el).cursor === 'pointer' && !el.closest('.aio-media-list');
        });
        showMore.forEach(el => {
          el.remove();
        })
      }
    },

    unwrapEntityMentions() {
      // Google wraps entity mentions inside the answer text ("Kirk Cameron drew
      // criticism...") in a span with role="button" that opens its side panel.
      // Bootstrap gives every role="button" a pointer cursor, so the sweep at
      // the end of applyPersistentHtmlChanges deleted the name and left the
      // sentence without its subject (DM-10562). Turn them into plain text
      // first. Chrome spans with role="button" carry an aria-label (Close,
      // Back) and stay for the sweep, as do mention cards (the ones wrapping
      // a div or image) that hold nothing but the name.
      this.dialogContent.querySelectorAll('span[role="button"]:not([aria-label])').forEach(span => {
        if (!span.textContent.trim()) {
          return;
        }
        if (span.querySelector('div, img, button, a, [role]')) {
          // A mention card can wrap a whole bullet: the person's photo, their
          // name mention and the sentence after it ("Olivia: Born in 2001...").
          // Drop its role so the sweep keeps the bullet (DM-11171); the name
          // mention inside is unwrapped when the loop reaches it. The class
          // keeps the bullets' role="list" out of formatPaaNativeLayout. The
          // photo is tagged so the styles below show it as a thumbnail beside
          // the text; without Google's stylesheet it renders full width.
          // PAA only, the popup this was reported and verified on.
          const name = span.querySelector('span[role="button"]:not([aria-label])');
          if (this.isPAA && name && name.textContent.trim() !== span.textContent.trim()) {
            span.removeAttribute('role');
            span.classList.add('aio-mention-card');
            span.querySelectorAll('img').forEach(img => {
              // Skip citation-chip favicons, and further images of a photo
              // already tagged.
              if (img.closest('button') || img.closest('.aio-mention-photo')) {
                return;
              }
              let photo = img;
              while (photo.parentElement !== span && !photo.parentElement.textContent.trim()) {
                photo = photo.parentElement;
              }
              photo.classList.add('aio-mention-photo');
              photo.parentElement.classList.add('aio-mention-row');
              this.fixLazyLoadedImages(photo);
            });
          }
          return;
        }
        const underline = span.closest('u');
        span.replaceWith(...span.childNodes);
        // Google's dotted-underline wrapper has no stylesheet here and would
        // render as a plain underline: looks like a link, does nothing.
        if (underline && !underline.querySelector('[role="button"]')) {
          underline.replaceWith(...underline.childNodes);
        }
      });
    },

    preserveCitationChips() {
      // Google attributes each AIO statement with inline citation chips
      // ("HOLA +2", "Reddit +3", ...) rendered as <button>s. Convert them to
      // inert styled pills so removeButtons()/the pointer sweep don't delete
      // them and the modal matches Google's expanded answer.
      const chips = this.dialogContent.querySelectorAll('button[aria-label*="View related links"]');
      chips.forEach(chip => {
        const pill = document.createElement('span');
        pill.className = 'aio-citation-pill';
        while (chip.firstChild) {
          pill.appendChild(chip.firstChild);
        }
        this.fixLazyLoadedImages(pill);
        chip.replaceWith(pill);
      });
    },

    formatPaaNativeLayout() {
      // The poller collapses PAA answers after expanding them, so html stored
      // before the parser-side style-strip carries display:none/opacity:0 on
      // the answer root — the .aio-paa-answer-root CSS unhides it (!important
      // to beat the stored inline style) so historical polls render too.
      const answerRoot = this.dialogContent.firstElementChild;
      if (answerRoot) {
        answerRoot.classList.add('aio-paa-answer-root');
      }
      // Match Google's expanded PAA answer: section headings stand out and the
      // horizontally-scrolling source-card strip (role="list") is dropped —
      // sourcing is carried by the inline citation chips instead.
      this.dialogContent.querySelectorAll('[role="heading"]').forEach(h => {
        h.classList.add('aio-paa-heading');
      });
      // Drop the source-card strips (role="list" with title/snippet text,
      // hidden in the natural view). Media carousels were tagged
      // .aio-media-list earlier and stay, as do lists of answer bullets built
      // from mention cards (DM-11171).
      this.dialogContent.querySelectorAll('[role="list"]:not(.aio-media-list)').forEach(rl => {
        if (rl.querySelector('.aio-mention-card')) {
          return;
        }
        rl.remove();
      });
    },

    tagMediaLists() {
      // Google puts two kinds of role="list" strips inside PAA answers:
      // media carousels (image thumbnails, no real text) which the natural
      // view SHOWS, and source-card strips (title/snippet text) which it
      // HIDES. Tag the former so the cleanup passes leave them alone.
      this.dialogContent.querySelectorAll('[role="list"]').forEach(rl => {
        if ((rl.textContent || '').trim().length <= 100) {
          rl.classList.add('aio-media-list');
          this.fixLazyLoadedImages(rl);
        }
      });
    },

    applyMinWidth() {
      const firstDiv = this.dialogContent.querySelector('div');
      if (firstDiv) {
        Object.assign(firstDiv.style, this.isPAA
          ? { maxWidth: "600px", minWidth: "500px" }
          : { width: "min(60rem, 90vw)", paddingTop: "0.75rem" }
        );
      }
    },

    handleScroll() {
      const targetDiv = this.dialogContent.querySelector('div');
      if (targetDiv) {
        targetDiv.style.height = "250px";
        targetDiv.style.overflowY = "clip";
      }
    },

    removeTitle() {
      const titles = this.dialogContent.querySelectorAll('h1');
      if (titles) {
        titles.forEach(title =>
          title.remove());
      } else {
        const titles = this.dialogContent.querySelectorAll('strong');
        if (titles) {
          titles.forEach(title =>
            title.remove());
        }
      }

      const headings = [...this.dialogContent.querySelectorAll('div > div[aria-level="2"][role="heading"]')]
        .map(heading => heading.parentElement);
      if (headings) {
        headings.forEach(heading => heading.remove());
      }
    },

    removeTopPadding() {
      const divs = [...this.dialogContent.querySelectorAll('div')];

      const paddingDiv = divs.find(div => div.style.paddingTop.includes("%"));
      if (paddingDiv) {
        paddingDiv.style.paddingTop = "0";
      }

      const marginDiv = divs.find(div => parseFloat(div.style.marginTop) < 0);
      if (marginDiv) {
        marginDiv.style.marginTop = "0";
      }
    },

    fixGridElements() {
      let gridElements = this.dialogContent.querySelectorAll('div');
      gridElements = Array.from(gridElements).filter(el => window.getComputedStyle(el).gridColumn.replace(/\s/g, '') === '2/-2');
      if (gridElements.length > 0) {
        gridElements[0].style.gridColumn = "1/-2";
      
        let makeGridElement = this.dialogContent.querySelector('div[class="UxeQfc"]');
        if (makeGridElement) {
          makeGridElement.style.display = "grid";
          makeGridElement.style.gridTemplateColumns = "minmax(0, 1fr) 428px";
          makeGridElement.style.columnGap = "40px";
        }
      }
      return;
    },
    fixHighlightColor() {
      let markedText = this.dialogContent.querySelectorAll('mark');
      if (markedText) {
        markedText.forEach(el => {
          el.style.backgroundColor = '#d3e3fd';
        });
      }
    },

    removeSvgs() {
      let svgs = this.dialogContent ? this.dialogContent.querySelectorAll('svg') : null;
      if (svgs) {
        svgs.forEach(svg => {
          // Keep the link-icon glyph inside preserved citation pills
          if (svg.closest('.aio-citation-pill')) {
            return;
          }
          svg.remove();
        });
      }
    },

    removeButtons() {
      let buttons = this.dialogContent ? this.dialogContent.querySelectorAll('button') : null;
      if (buttons) {
        buttons.forEach(button => {
          button.remove();
        });
      }
      let buttonDivs = this.dialogContent ? this.dialogContent.querySelectorAll('div[role="button"]') : null;
      if (buttonDivs) {
        buttonDivs.forEach(buttonDiv => {
          if (buttonDiv.querySelector('img[alt="Related video thumbnail"]')) {
            return;
          }
          // Keep the image wrappers of preserved media carousels
          if (buttonDiv.closest('.aio-media-list')) {
            return;
          }
          buttonDiv.remove();
        });
      }

      let diveDeeper = this.dialogContent ? this.dialogContent.querySelector('div[data-query]') : null;
      if (diveDeeper) {
        let children = diveDeeper.children;
        if (children) {
          for (let child of children) {
            let computedStyle = window.getComputedStyle(child);
            if (computedStyle.contain.includes('paint')) {
              diveDeeper.remove();
            }
          }
        }
      }
    },

    fixLinks() {
      const linkContainer = this.dialogContent.querySelector('div[role="list"]');
      if (linkContainer) {
        // Set explicit max-width on the list container's parent to constrain it
        const listParent = linkContainer.parentElement;
        if (listParent) {
          listParent.style.maxWidth = '1000px';
          listParent.style.overflowX = 'auto';
        }

        linkContainer.style.display = "flex";
        linkContainer.style.flexWrap = "nowrap";
        linkContainer.style.overflowX = "auto";
        linkContainer.style.maxWidth = "max-content";
        const listItems = linkContainer.querySelectorAll('div[role*="listitem"]');
        listItems.forEach(element => {
          element.style.padding = '0 10px 0 0';
          element.style.flexShrink = '0';
          element.style.maxWidth = '40%';

          // Remove hardcoded widths from descendant divs
          const descendantDivs = element.querySelectorAll('div');
          descendantDivs.forEach(div => {
            if (div.style.width) {
              div.style.width = '';
            }
          });
          
        });
        const listItemsParent = listItems[0]?.parentElement;
          if (listItemsParent && listItemsParent !== linkContainer) {
            listItemsParent.style.display = 'flex';
            listItemsParent.style.flexWrap = 'nowrap';
            listItemsParent.style.overflowX = 'auto';
            listItemsParent.style.maxWidth = '100%';
          }

      }
    },

    removeUnwantedText() {
      const firstUnwantedTextElement = this.dialogContent.querySelector('div[jsaction="click:oTLhu"]');
      if (firstUnwantedTextElement) {
        firstUnwantedTextElement.parentElement?.remove();
      }

      this.removeGoogleDataNodes();

      const targetTexts = ['.', 'Opens in new tab'];
      const spanNodes = this.dialogContent.querySelectorAll('span');
      const newTabNodesArray = Array.from(spanNodes).filter(node =>
        targetTexts.includes(node.textContent.trim())
      );
      if (newTabNodesArray.length > 0) {
        newTabNodesArray.forEach(node => {
          node.remove();
        });
      }

      this.removeButtons();
      this.removeGBubbleElements();

      // Remove hidden "Related Links" header
      const h2Elements = this.dialogContent.querySelectorAll('h2');
      h2Elements.forEach(h2 => {
        if (h2.textContent.toLowerCase().trim() === 'related links') {
          h2.remove();
        }
      });

      // Remove absolute positioned overlay elements
      const allDivs = this.dialogContent.querySelectorAll('div');
      allDivs.forEach(div => {
        const style = window.getComputedStyle(div);
        if (style.position === 'absolute' && style.width === '100%') {
          const child = div.firstElementChild;
          if (child) {
            const childStyle = window.getComputedStyle(child);
            if (childStyle.left === '0px' && childStyle.position === 'relative' && parseInt(childStyle.zIndex) > 0) {
              div.remove();
            }
          }
        }
      });
    },

    addShowMoreButton() {
      const oldShowMoreButton = this.dialogContent.querySelector('div[aria-expanded="false"][role="button"]');

      if (oldShowMoreButton) {
        oldShowMoreButton.remove();
      }

      const newShowMoreButton = this.createToggleButton("Show More", "show-more-button");
      this.dialogContent.after(newShowMoreButton);

      this.handleShowMoreButtonClick();
    },

    handleShowMoreButtonClick() {
      const showMoreButton = this.dialogContent.parentElement.querySelector('.show-more-button');

      if (showMoreButton) {
        showMoreButton.addEventListener("click", () => {
          showMoreButton.remove();

          this.expandDialogContent();

          const showLessButton = this.createToggleButton("Show Less", "show-less-button");
          this.dialogContent.after(showLessButton);

          showLessButton.addEventListener("click", () => {
            showLessButton.remove();
            this.dialogContent.innerHTML = this.originalDialogHtml;
            this.applyPersistentHtmlChanges();
          });
        });
      }
    },

    expandDialogContent() {
      const allElements = this.dialogContent.querySelectorAll("*");
      this.setSize();
      this.displayHiddenElements(allElements);
      this.removeBlur(allElements);
      this.setImageSizes();
      if (this.isPAA) {
        this.removePaddingForPaa();
      }
      // Reveal elements hidden with ahbak/aria-hidden attributes (flex layout)
      this.revealHiddenFlexContent();
      this.removeGoogleDataNodes();
    },

    renderXpmValues() {
      this.dialogContent.querySelectorAll('span[data-xpm-latex]').forEach(span => {
        const value = span.getAttribute('data-xpm-latex').replace(/\\(\$)/g, '$1');
        span.replaceWith(document.createTextNode(value));
      });
    },

    removeGoogleDataNodes() {
      const walker = document.createTreeWalker(this.dialogContent, NodeFilter.SHOW_TEXT);
      const toRemove = [];
      let node;
      while ((node = walker.nextNode())) {
        const text = node.textContent.trimStart();
        if (text.startsWith('\\x3c!--') || node.textContent.trim() === "');") {
          toRemove.push(node);
        }
      }
      toRemove.forEach(n => n.remove());
    },

    revealHiddenFlexContent() {
      // Scope to rhs-col when present to avoid revealing hidden source containers
      // outside the grid that would bleed into the main content column
      const rhsCol = this.dialogContent.querySelector('[data-container-id="rhs-col"]');
      const context = rhsCol || this.dialogContent;
      context.querySelectorAll('[ahbak="true"]').forEach(el => {
        this.unhideElement(el);
        if (el.tagName === 'UL') {
          el.querySelectorAll('li').forEach(li => this.unhideElement(li));
        }
      });
    },

    formatExpandedView() {
      this.handleGridElements();

    },

    hasHorizontalSourcesList() {
      const hasFlexColumns = this.dialogContent.querySelector('[data-container-id="main-col"]')
        && this.dialogContent.querySelector('[data-container-id="rhs-col"]');
      if (hasFlexColumns) return false;
      // Horizontal scroll layout has a role="list" container with role="listitem" children
      // This is the sources list that scrolls horizontally at the bottom
      const roleList = this.dialogContent.querySelector('[role="list"]');
      return !!roleList;
    },

    revealHiddenSourceContainers() {
      // Helper to check if an element is inside a hidden div
      const isInsideHiddenDiv = (el) => {
        let parent = el.parentElement;
        while (parent && parent !== this.dialogContent) {
          if (parent.style.display === 'none') {
            return true;
          }
          parent = parent.parentElement;
        }
        return false;
      };

      // Helper to extract normalized URL from an li element for deduplication
      const getUrlFromLi = (li) => {
        const anchor = li.querySelector('a[href]');
        return anchor ? this.normalizeUrl(anchor.getAttribute('href')) : null;
      };

      // Find the visible sources ul - look for the ul with class 'zVKf0d' or containing li.LLtSOc
      let visibleUl = this.dialogContent.querySelector('ul.zVKf0d');

      // Check if the found ul is actually visible (not inside a hidden container)
      if (visibleUl && isInsideHiddenDiv(visibleUl)) {
        visibleUl = null;
      }

      if (!visibleUl) {
        // Fallback: find a ul that contains visible source li elements and is not hidden
        const allUls = this.dialogContent.querySelectorAll('ul');
        for (const ul of allUls) {
          if (!isInsideHiddenDiv(ul) && ul.querySelector('li a[href]')) {
            visibleUl = ul;
            break;
          }
        }
      }

      if (!visibleUl) return;

      // Collect existing URLs from the visible list to avoid duplicates
      const existingUrls = new Set();
      visibleUl.querySelectorAll('li').forEach(li => {
        const url = getUrlFromLi(li);
        if (url) {
          existingUrls.add(url);
        }
      });

      // Find any divs with display:none that contain source lists
      const allDivs = this.dialogContent.querySelectorAll('div');
      allDivs.forEach(div => {
        if (div.style.display === 'none') {
          // Use querySelectorAll to find ALL ul elements (not just the first one)
          // Some formats have multiple ul elements, each with a single li
          const hiddenUls = div.querySelectorAll('ul');
          if (hiddenUls.length > 0) {
            hiddenUls.forEach(hiddenUl => {
              if (hiddenUl.querySelector('li a[href]')) {
                // Merge hidden sources into the visible list
                hiddenUl.querySelectorAll('li').forEach(li => {
                  // Only clone li elements that have actual content (links)
                  if (li.querySelector('a[href]')) {
                    // Check for duplicate URL before adding
                    const url = getUrlFromLi(li);
                    if (url && existingUrls.has(url)) {
                      return; // Skip duplicate
                    }
                    if (url) {
                      existingUrls.add(url); // Track this URL to avoid future duplicates
                    }
                    const clonedLi = li.cloneNode(true);
                    // Ensure the cloned li has the same class as visible ones
                    if (!clonedLi.classList.contains('LLtSOc')) {
                      clonedLi.classList.add('LLtSOc');
                    }
                    this.copyBorderDivToLi(visibleUl, clonedLi);
                    this.applyFlexStylingToLi(clonedLi);
                    visibleUl.appendChild(clonedLi);
                  }
                });
              }
            });
            // Remove the hidden container after extracting sources
            div.remove();
          }
        }
      });
    },

    handleGridElements() {
      const gridElement = this.dialogContent.querySelector('[style*="display: grid"]');

      // Check for flex layout (non-grid format) FIRST - before revealing hidden containers
      // This is important because revealHiddenSourceContainers can add inline citations
      // from main-col that have different format than the proper source cards in rhs-col
      if (!gridElement) {
        const mainCol = this.dialogContent.querySelector('[data-container-id="main-col"]');
        const rhsCol = this.dialogContent.querySelector('[data-container-id="rhs-col"]');

        if (mainCol && rhsCol) {
          // This is a flex layout - handleFlexLayout will process sources correctly
          this.handleFlexLayout();
          return [];
        }
      }

      // For grid layouts (and other non-flex layouts), reveal hidden source containers
      this.revealHiddenSourceContainers();

      // Check for flex layout again for non-grid, non-flex-with-cols layouts
      if (!gridElement) {
        this.handleFlexLayout();
        return [];
      }

      const firstChild = gridElement.children[0];
      let secondChild = gridElement.children[1];
      let gridMissing = false;
      if (!secondChild) {
        gridMissing = true;
        const divs = this.dialogContent.querySelectorAll('div');
        divs.forEach(div => {
          const ul = div.querySelector(':scope > ul');
          if (ul) {
            const liWithAnchor = Array.from(ul.querySelectorAll('li')).some(li => {
              const anchor = li.querySelector('[href]');
              return anchor && !anchor.href.includes('google');
            });
            if (liWithAnchor) {
              secondChild = div;
            }
          }
        })

        gridElement.appendChild(secondChild);
        const aimThreadsElement = gridElement.querySelector('[jsaction*="aimThreadsViewStateChange"]');
        if (aimThreadsElement) {
          aimThreadsElement.remove();
        }
      }
      secondChild.style.backgroundColor = '#F1F6FF';
      const secondChildUl = secondChild.querySelector('ul');
      let secondChildUlParent = secondChildUl ? secondChildUl.parentElement : null;

      if (!firstChild || !secondChild) {
        return [];
      }

      if (!gridMissing) {
        while (secondChildUlParent.firstChild) {
          secondChildUlParent.removeChild(secondChildUlParent.firstChild);
        }
      }

      const ulArray = this.buildSecondColumnOfGrid(firstChild, secondChild);

      this.removeDuplicateListItems(ulArray, secondChildUlParent, gridMissing);

      this.formatListItems(secondChildUlParent);
    },

    handleFlexLayout() {
      // Look for the flex layout structure with main-col and rhs-col
      const mainCol = this.dialogContent.querySelector('[data-container-id="main-col"]');
      const rhsCol = this.dialogContent.querySelector('[data-container-id="rhs-col"]');

      if (!mainCol || !rhsCol) {
        return;
      }

      // Expand hidden sources before restructuring the layout
      this.expandFlexSources(rhsCol);

      // Merge any duplicate UL elements into a single one
      this.mergeMultipleSourceLists(rhsCol);

      this.moveMainColSourceStrip(mainCol, rhsCol);

      // Consolidate sources from main-col into rhs-col
      // Some formats have source links (li.LLtSOc or similar) scattered in main content
      this.consolidateMainColSources(mainCol, rhsCol);

      // RSO-embedded (pOOWX) AI Overview also repeats the rhs-col sources as an inline
      // card grid (a.Zbfntb) in main-col — remove it so the layout matches top-of-page. DM-8611
      this.removeInlineSourceCards(mainCol, rhsCol);

      // Find the common ancestor wrapper (pWvJNd class)
      const wrapper = this.dialogContent.querySelector('.pWvJNd');
      if (wrapper) {
        // In some HTML layouts, rhs-col is in a separate sibling wrapper
        // We need to remove that wrapper after moving rhs-col
        const rhsOriginalParent = rhsCol.parentElement;

        // Set up grid layout
        wrapper.style.display = 'grid';
        wrapper.style.gridTemplateColumns = 'minmax(0, 1fr) 428px';
        wrapper.style.columnGap = '40px';

        // Remove all existing children from wrapper
        while (wrapper.firstChild) {
          wrapper.removeChild(wrapper.firstChild);
        }

        // Append in correct order: main content (left), sources (right)
        wrapper.appendChild(mainCol);
        wrapper.appendChild(rhsCol);

        // Remove the original parent of rhs-col if it's now empty and not the same as wrapper
        if (rhsOriginalParent && rhsOriginalParent !== wrapper && rhsOriginalParent.children.length === 0) {
          rhsOriginalParent.remove();
        }
      }

      // Style the sources column - reset any positioning that might cause overlap
      rhsCol.style.position = 'static';
      rhsCol.style.float = 'none';
      rhsCol.style.backgroundColor = '#F1F6FF';
      rhsCol.style.padding = '1rem';
      rhsCol.style.borderRadius = '8px';
      rhsCol.style.width = '100%';
      rhsCol.style.maxWidth = '428px';

      // Format the source list items - try multiple selectors for different HTML formats
      const sourceItems = rhsCol.querySelectorAll('li');
      sourceItems.forEach((li) => {
        li.style.margin = '1rem 0';
        li.style.borderBottom = '1px solid #d3e3fd';
        li.style.paddingBottom = '1rem';
        li.style.listStyleType = 'none';

        // The anchor container is div.MFrAxb - make it the positioning context
        const anchorContainer = li.querySelector('div.MFrAxb, div.fSJHKf');
        if (anchorContainer) {
          anchorContainer.style.position = 'relative';
        }

        // Make the anchor overlay clickable
        const anchor = li.querySelector('a.NDNGvf, a.POYEld');
        if (anchor) {
          // Ensure anchor is clickable and opens in new tab
          anchor.removeAttribute('tabindex');
          anchor.setAttribute('target', '_blank');
          Object.assign(anchor.style, {
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            zIndex: '10',
            cursor: 'pointer'
          });
        }

        // Fix images - load from data-src if src is placeholder
        this.fixLazyLoadedImages(li);

        // Make the content container a flexbox with image on the right
        this.applyFlexStylingToLi(li);
      });
    },

    expandFlexSources(rhsCol) {
      // Find the hidden dialog containing expanded sources (may be hidden via style="display:none")
      let hiddenDialog = rhsCol.querySelector('[role="dialog"]');

      // Also look for any div with display:none that contains a ul with sources
      // (new format doesn't use role="dialog")
      if (!hiddenDialog) {
        const allDivs = rhsCol.querySelectorAll('div');
        for (const div of allDivs) {
          if (div.style.display === 'none' && div.querySelector('ul li a[href]')) {
            hiddenDialog = div;
            break;
          }
        }
      }

      if (hiddenDialog) {
        hiddenDialog.style.display = ''; // Unhide the dialog first
      }
      const expandedList = hiddenDialog ? hiddenDialog.querySelector('ul') : null;

      // Find the visible sources container (has ahbak="true" initially) and its ul
      // It's a sibling of the span containing the dialog, not inside the dialog
      const visibleContainer = rhsCol.querySelector('[data-xid="aim-aside-initial-corroboration-container"]');
      let visibleSourcesList = visibleContainer ? visibleContainer.querySelector('ul') : null;

      // Case 1: We have both a visible container AND a dialog with more sources
      // Replace the visible container's ul with the expanded list from the dialog
      if (visibleContainer && hiddenDialog && expandedList) {
        // Unhide the visible container
        this.unhideElement(visibleContainer);

        // Clear the visible container and replace with expanded list content
        if (visibleSourcesList) {
          // IMPORTANT: Before clearing, save any hidden li elements (ahbak="true")
          // These might contain sources not present in the dialog
          const hiddenLiElements = [];
          visibleSourcesList.querySelectorAll('li[ahbak="true"]').forEach(li => {
            hiddenLiElements.push(li.cloneNode(true));
          });

          // Clear existing items
          while (visibleSourcesList.firstChild) {
            visibleSourcesList.removeChild(visibleSourcesList.firstChild);
          }

          // Copy all items from the expanded list
          expandedList.querySelectorAll('li').forEach(li => {
            const clonedLi = li.cloneNode(true);
            this.unhideElement(clonedLi);
            visibleSourcesList.appendChild(clonedLi);
          });

          // Re-add the previously hidden li elements (now unhidden)
          hiddenLiElements.forEach(li => {
            this.unhideElement(li);
            visibleSourcesList.appendChild(li);
          });
        }

        // Remove the dialog after extracting sources
        hiddenDialog.remove();

        // Remove duplicate sources
        this.removeDuplicateSourcesFromFlexLayout(visibleSourcesList);
        return;
      }

      // Case 2: No visible container but dialog has the full list
      // Extract the ul from the dialog and place it directly in rhsCol
      if (!visibleContainer && expandedList && hiddenDialog) {
        const dialogParent = hiddenDialog.parentElement;
        if (dialogParent) {
          // Unhide all items
          expandedList.querySelectorAll('li').forEach(li => this.unhideElement(li));
          // Move the ul out of the dialog
          dialogParent.insertBefore(expandedList, hiddenDialog);
          hiddenDialog.remove();
          visibleSourcesList = expandedList;

          // Remove duplicate sources
          this.removeDuplicateSourcesFromFlexLayout(visibleSourcesList);
          return;
        }
      }

      // Case 3: Fallback - find any ul that's NOT inside the dialog
      if (!visibleSourcesList) {
        const allUls = rhsCol.querySelectorAll('ul');
        for (const ul of allUls) {
          if (!hiddenDialog || !hiddenDialog.contains(ul)) {
            visibleSourcesList = ul;
            break;
          }
        }
      }

      if (visibleSourcesList) {
        // Unhide the visible container if it exists and was hidden
        if (visibleContainer) {
          this.unhideElement(visibleContainer);
        }

        // Unhide any li elements that are individually hidden with ahbak="true"
        // Some formats hide additional sources with ahbak attribute on the li itself,
        // rather than in a separate hidden container
        visibleSourcesList.querySelectorAll('li[ahbak="true"]').forEach(li => {
          this.unhideElement(li);
        });

        // Remove duplicate sources
        this.removeDuplicateSourcesFromFlexLayout(visibleSourcesList);
      }
    },

    mergeMultipleSourceLists(rhsCol) {
      // Find all UL elements in rhsCol that contain source items
      const allUls = rhsCol.querySelectorAll('ul');
      if (allUls.length <= 1) {
        return; // Nothing to merge
      }

      // Use the first UL as the primary list
      const primaryUl = allUls[0];

      // Collect existing URLs from the primary list to avoid duplicates
      const existingUrls = new Set();
      primaryUl.querySelectorAll('li').forEach(li => {
        const anchor = li.querySelector('a[href]');
        const normalizedUrl = anchor ? this.normalizeUrl(anchor.getAttribute('href')) : null;
        if (normalizedUrl) {
          existingUrls.add(normalizedUrl);
        }
      });

      // Merge items from other ULs into the primary one
      for (let i = 1; i < allUls.length; i++) {
        const ul = allUls[i];
        ul.querySelectorAll('li').forEach(li => {
          const anchor = li.querySelector('a[href]');
          const normalizedUrl = anchor ? this.normalizeUrl(anchor.getAttribute('href')) : null;

          if (normalizedUrl && !existingUrls.has(normalizedUrl)) {
            existingUrls.add(normalizedUrl);
            const clonedLi = li.cloneNode(true);
            this.unhideElement(clonedLi);
            primaryUl.appendChild(clonedLi);
          }
        });

        // Remove the duplicate UL
        ul.remove();
      }
    },

    moveMainColSourceStrip(mainCol, rhsCol) {
      // Some layouts leave rhs-col empty and show the sources only as a horizontal
      // card strip (role="list" of role="listitem") in main-col — move those cards
      // into rhs-col as a ul so they get the sources-column formatting. DM-11290
      if (rhsCol.querySelector('a[href]')) {
        return;
      }
      // Anchor on the source-card link: main-col can also hold role="list" image carousels
      const strip = mainCol.querySelector('[role="listitem"] a.POYEld')?.closest('[role="list"]');
      if (!strip) {
        return;
      }
      const ul = document.createElement('ul');
      strip.querySelectorAll('[role="listitem"]').forEach(item => {
        const li = document.createElement('li');
        li.append(...item.childNodes);
        // Source-name row above the title, the thumbnail beside the title
        li.querySelectorAll('.YoHdkc').forEach(el => { el.style.flexDirection = 'column'; });
        li.querySelectorAll('.Qh3Lh').forEach(el => {
          Object.assign(el.style, { display: 'flex', justifyContent: 'space-between', gap: '1rem' });
        });
        ul.appendChild(li);
      });
      rhsCol.appendChild(ul);
      strip.remove();
    },

    consolidateMainColSources(mainCol, rhsCol) {
      // Find the target sources list in rhs-col
      let rhsSourcesList = rhsCol.querySelector('ul');
      if (!rhsSourcesList) {
        return;
      }

      // Collect existing URLs from rhs-col to avoid duplicates
      const existingUrls = new Set();
      rhsSourcesList.querySelectorAll('li a[href]').forEach(anchor => {
        const normalizedUrl = this.normalizeUrl(anchor.getAttribute('href'));
        if (normalizedUrl) {
          existingUrls.add(normalizedUrl);
        }
      });

      // Find source list items in main-col (li elements with anchors that look like sources)
      // These have classes like 'LLtSOc', 'CyMdWb', etc. and contain external links
      // Source cards have class 'LLtSOc' or 'CyMdWb' - distinguish from inline entity links
      const mainColUls = mainCol.querySelectorAll('ul');
      const itemsToRemove = [];

      mainColUls.forEach(ul => {
        const liElements = ul.querySelectorAll('li');
        liElements.forEach(li => {
          // Only process source cards (li with class LLtSOc or CyMdWb), not inline entity descriptions
          // Inline entity descriptions have structure: li > span > strong > a (text content)
          // Source cards have structure: li.LLtSOc > div.MFrAxb > a.NDNGvf (card with image)
          const isSourceCard = li.classList.contains('LLtSOc') || li.classList.contains('CyMdWb');
          if (!isSourceCard) {
            return; // Skip inline entity descriptions
          }

          const anchor = li.querySelector('a[href]');
          if (anchor) {
            const href = anchor.getAttribute('href');
            if (href && !href.includes('google.com/search') && (href.startsWith('http') || href.startsWith('/'))) {
              const normalizedUrl = this.normalizeUrl(href);

              if (normalizedUrl && !existingUrls.has(normalizedUrl)) {
                existingUrls.add(normalizedUrl);
                // Clone and add to rhs sources list
                const clonedLi = li.cloneNode(true);
                if (!clonedLi.classList.contains('LLtSOc')) {
                  clonedLi.classList.add('LLtSOc');
                }
                rhsSourcesList.appendChild(clonedLi);
              }

              // Mark this source card for removal from main-col
              itemsToRemove.push(li);
            }
          }
        });
      });

      // Remove source cards from main-col after copying to rhs-col
      itemsToRemove.forEach(li => li.remove());
    },

    removeInlineSourceCards(mainCol, rhsCol) {
      if (!rhsCol || !rhsCol.querySelector('a[href]')) {
        return;
      }
      const cards = Array.from(mainCol.querySelectorAll('a.Zbfntb'));
      if (!cards.length) {
        return;
      }
      // Smallest common container of all the source-card links...
      let grid = cards[0];
      while (grid && !cards.every(c => grid.contains(c))) {
        grid = grid.parentElement;
      }
      while (grid && grid.parentElement && grid.parentElement !== mainCol
        && grid.parentElement.querySelectorAll('a.Zbfntb').length === cards.length
        && !grid.parentElement.querySelector('ul')) {
        grid = grid.parentElement;
      }
      if (grid && grid !== mainCol) {
        grid.remove();
      }
    },

    removeDuplicateSourcesFromFlexLayout(sourcesList) {
      const existingUrls = new Set();
      const itemsToRemove = [];

      sourcesList.querySelectorAll('li').forEach(li => {
        const anchor = li.querySelector('a[href]');
        const normalizedUrl = anchor ? this.normalizeUrl(anchor.getAttribute('href')) : null;

        if (normalizedUrl) {
          if (existingUrls.has(normalizedUrl)) {
            itemsToRemove.push(li);
          } else {
            existingUrls.add(normalizedUrl);
          }
        }
      });

      itemsToRemove.forEach(item => item.remove());
    },

    buildSecondColumnOfGrid(firstChild, secondChild) {
      const ulArray = [];
      const divs = firstChild.querySelectorAll('div');
      divs.forEach(div => {
        const ulElements = div.querySelectorAll(':scope > ul');
        if (ulElements.length >= 2) {
          ulElements.forEach(ul => {
            if (ul.querySelectorAll('a').length > 0) {
              ulArray.push(ul);
            }
          });
        }
      });

      if (ulArray.length === 0) {
        const liElements = secondChild.querySelectorAll('li');
        if (liElements.length > 0) {
          liElements.forEach(li => {
            if (li.querySelectorAll('a').length > 0) {
              ulArray.push(li);
            }
          });
        }
      }

      return ulArray;
    },

    removeDuplicateListItems(ulArray, secondChildUlParent, gridMissing) {
      let existingHrefs = new Set();
      let itemsToRemove = [];

      ulArray.forEach(ul => {
        const aTags = ul.querySelectorAll('a');
        aTags.forEach(a => {
          const href = a.getAttribute('href') || '';
          let index = href.indexOf('~:text%3D');
          if (index === -1) {
            index = href.indexOf('ved=');
            if (index === -1) {
              index = href.indexOf('text=');
            }
          }
          const strippedHref = index !== -1 ? href.substring(0, index) : href;
          if (!existingHrefs.has(strippedHref)) {
            existingHrefs.add(strippedHref);
            if (!gridMissing) {
              secondChildUlParent.appendChild(ul);
            }
          } else {
            itemsToRemove.push(ul);
          }
        });
      });

      itemsToRemove.forEach(item => {
        item.remove();
      })
    },

    formatListItems(secondChildUlParent) {
      this.setImageSizes();
      const ulsInParent = secondChildUlParent.querySelectorAll('ul');
      ulsInParent.forEach(ul => {
        const liTags = ul.querySelectorAll('li');
        liTags.forEach(li => {
          if (!li.querySelector('a')) {
            li.remove();
          }
          li.style.position = 'relative';
          li.style.margin = '1rem';
          li.style.borderBottom = '1px solid #d3e3fd';

          li.style.listStyleType = 'none';
          let anchorChildren = li.querySelectorAll('a');

          anchorChildren.forEach(a => {
            let nextSibling = a.nextElementSibling;
            // find the element with text that isn't the title and make the text smaller
            if (nextSibling && nextSibling.tagName === 'DIV') {
              let secondChild = nextSibling.children[1];

              if (secondChild) {
                secondChild.style.fontSize = '0.875rem';
              } else {
                const firstChild = nextSibling.firstElementChild.firstElementChild;
                if (firstChild) {
                  const secondChild = firstChild.children[1];
                  if (secondChild) {
                    secondChild.style.fontSize = '0.875rem';
                  }
                }
              }
            }
          });
          // for some reason the src attribute gets removed sometimes, however the base64 is saved in data-src so we copy that over
          this.fixLazyLoadedImages(li);
        });
        const aTags = ul.querySelectorAll('a');
        // each li basically has an overlay that is an <a> tag - in order for the link to work, it has to be on top
        aTags.forEach(a => {
          Object.assign(a.style, {
            position: 'absolute',
            width: '100%',
            height: '100%',
            zIndex: '1',
            lineClamp: '4'
          });
          const nextSibling = a.nextElementSibling;
          if (nextSibling) {
            const nextSiblingChildren = nextSibling.children.length;
            const images = nextSibling.querySelectorAll('img');
            if (nextSiblingChildren === 1) {
              const child = nextSibling.firstElementChild;
              if (child && images.length > 1) {
                child.style.display = 'flex';
                child.style.gap = '1rem';
              }
            } else if (images.length > 1) {
              nextSibling.style.display = 'flex';
              nextSibling.style.gap = '1rem';
            }
            const firstChild = nextSibling.firstElementChild;
            if (firstChild) {
              const gChild = firstChild.firstElementChild;
              if (gChild) {
                const textElement = gChild.firstElementChild;
                if (textElement) {
                  Object.assign(textElement.style, {
                    display: '-webkit-box',
                    WebkitLineClamp: '2',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  });
                }
              }
            }
          }
        });
      });
    },

    removeGBubbleElements() {
      const gBubble = document.querySelectorAll('g-bubble');
      if (gBubble) {
        gBubble.forEach(bubble => {
          bubble.remove();
        });
      }
    },

    setSize() {
      const firstDiv = this.dialogContent.querySelector('div');
      if (!firstDiv) {
        return;
      }
      firstDiv.style.overflowY = 'visible';
      firstDiv.style.minHeight = '450px';
      if (this.isPAA) {
        firstDiv.style.minWidth = '652px';
      }
    },
    displayHiddenElements(allElements) {
      const hiddenYElements = Array.from(allElements).filter(el =>
        window.getComputedStyle(el).overflowY === "clip"
      );
      hiddenYElements.forEach((el) => {
        el.style.overflowY = 'visible';
      });
    },
    removeBlur(allElements) {
      const blur = Array.from(allElements).filter(el =>
        window.getComputedStyle(el).backgroundImage !== 'none' &&
        window.getComputedStyle(el).backgroundImage.trim().includes('linear-gradient'));
      if (blur.length > 0) {
        blur.forEach(el => el.style.backgroundImage = 'none');
      }
    },
    setImageSizes() {
      let liElements = Array.from(this.dialogContent.querySelectorAll('li'));
      liElements.forEach(li => {
        let images = li.querySelectorAll('img');
        if (images.length === 1) {
          images.forEach(img => {
            const src = img.getAttribute('src').toLowerCase() || '';
            if ((img.parentElement.tagName !== 'G-IMG' && img.height <= "50") || src.includes('favicon') ) {
              img.style.maxWidth = "16px";
              img.style.maxHeight = "16px";
            }
          })
        } else if (images.length === 2) {
          images[0].style.maxWidth = "16px";
          images[0].style.maxHeight = "16px";
          images[1].style.maxWidth = "80px";
          images[1].style.maxHeight = "80px";
        }
      })
    },
    removePaddingForPaa() {
      const links = Array.from(this.dialogContent.querySelectorAll('div[role="listitem"]'));
      if (links.length > 0) {
        links.forEach(link => {
          const paddedDivs = Array.from(link.querySelectorAll('div')).filter(div =>
            div.style.paddingTop.includes("%")
          );
          if (paddedDivs.length > 0) {
            paddedDivs.forEach(paddedDiv => {
              paddedDiv.style.paddingTop = "0";
            })
          }
        })
      }
    },
  },
  watch: {
    isOpenDialog(newValue) {
      if (newValue) {
        this.onDialogShow(newValue);
      }
    },
  },
  mounted() {
  }
}
</script>

<style lang="scss" scoped>
// DM-10271: PAA question, its rating and its sources — sizes and colours from the ticket's prototype
.paa-dialog :deep(.draggable-container) {
  width: 860px;
  max-width: calc(100vw - 32px);
  font-size: 12px;
  color: #212529;
}

.paa-date {
  margin-right: 2.5rem; // clear of the dialog's close button
  color: #40454C;
  font-weight: normal;
}

.paa-question {
  display: inline-block;
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
  padding: 3px 8px;
  border-radius: 4px;
}

// the answer text at the prototype's size, html or plain
.paa-plain-answer,
.paa-dialog .dialog-content {
  font-size: 14px;
  line-height: 1.45;
}

.paa-rating-box {
  margin-top: 10px;
  border: 1px solid #c5d6f3;
  border-radius: 6px;
  padding: 7px 11px 11px;

  .paa-rating-head {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }

  .paa-rating-title {
    font-weight: 700;
  }

  .paa-rating-help {
    font-size: 11px;
    color: #40454C;
  }

  .paa-rating-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 5px;
  }
}

// the Markup dialog's rating button (MarkupEditorDialog .btn)
.paa-rating-btn {
  width: 97px;
  height: 28px;
  border: 2px solid;
  border-radius: 4px;
  font: inherit;
  padding: 0;

  &:disabled {
    opacity: 1;
  }
}

.paa-sources {
  margin-top: 10px;
  display: flex;
  flex-direction: column;

  .paa-sources-head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 8px;
    padding: 0 8px 5px;
    font-weight: 700;
  }

  .paa-sources-title {
    flex: 1 1 240px;
  }

  .paa-chip-column {
    width: 122px;
  }
}

.paa-source-row {
  min-height: 44px;
  box-sizing: border-box;
  border-top: 1px solid #dee2e6;
  padding: 4px 8px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  .paa-source-main {
    flex: 1 1 240px;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .paa-source-text {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;

    a {
      color: #003C8F;
    }

    a, span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.dialog-content {
  overflow-y: auto;
  overflow-x: hidden;

  // Inline citation chips preserved from Google's AIO html ("Bloomberg +2", ...)
  :deep(.aio-citation-pill) {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background-color: #f1f3f4;
    border-radius: 16px;
    padding: 2px 8px;
    margin: 0 4px;
    font-size: 12px;
    line-height: 16px;
    color: #3c4043;
    white-space: nowrap;
    vertical-align: middle;
    cursor: default;

    img {
      width: 14px;
      height: 14px;
      border-radius: 50%;
    }
  }

  // Mention-card photos as thumbnails beside their bullet (DM-11171). Google's
  // square box (padding-top:100%, img absolutely positioned) needs a sized,
  // positioned frame or it fills the popup width. The height is explicit
  // because removeTopPadding() zeroes the first %-padding it finds.
  :deep(.aio-mention-row) {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  :deep(.aio-mention-photo) {
    flex: 0 0 48px;
    width: 48px;
    height: 48px;
    position: relative;
    border-radius: 8px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
    }
  }

  :deep(.aio-paa-heading) {
    font-weight: bold;
    font-size: 1.15em;
    margin: 1rem 0 0.5rem;
  }

  // Media carousel kept from the natural view — a row of thumbnails.
  // Google nests the listitems in a wrapper div, so flex both levels.
  :deep(.aio-media-list),
  :deep(.aio-media-list > div:not([role="listitem"])) {
    display: flex;
    align-items: center;
    gap: 8px;
    overflow-x: auto;
    max-width: 100%;
    margin: 8px 0;
  }

  :deep(.aio-media-list [role="listitem"]) {
    flex-shrink: 0;
  }

  :deep(.aio-media-list img) {
    height: 120px;
    width: auto;
    border-radius: 8px;
    flex-shrink: 0;
  }

  // Unhide the PAA answer root — html stored before the parser-side style
  // strip carries Google's collapsed-state inline style, hence !important
  :deep(.aio-paa-answer-root) {
    display: block !important;
    opacity: 1 !important;
    position: static !important;
  }
}
</style>